/**
 * Memoized Redux Selectors
 *
 * These selectors prevent unnecessary re-renders by using memoization.
 * Replace all inline useSelector calls with these optimized versions.
 */

import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "./index";

// Question Input Selectors
export const selectAnswerGenerationActive = (state: RootState) => state.questionInput.answerGenerationActive;
export const selectRequestedToForceStop = (state: RootState) => state.questionInput.requestedToForceStopAIResponseGeneration;
export const selectIsSidebarOpen = (state: RootState) => state.questionInput.isSidebarOpen;
export const selectTemplateAppliedFlag = (state: RootState) => state.questionInput.templateAppliedFlag;
export const selectIsSessionSearchActive = (state: RootState) => state.questionInput.isSessionSearchActive;
export const selectFileAttachments = (state: RootState) => state.questionInput.fileAttachments;
export const selectFileUploadOrDeleteAPITriggerActive = (state: RootState) => state.questionInput.fileUploadOrDeleteAPITriggerActive;
export const selectActiveFileAttachmentTour = (state: RootState) => state.questionInput.activeFileAttachmentTour;

// All Product Suggestions Panel Selectors
export const selectSuggestionsStage = (state: RootState) => state.allProductSuggestions.stage;
export const selectSuggestionsMatchedProducts = (state: RootState) => state.allProductSuggestions.matchedProducts;
export const selectSuggestionsMessage = (state: RootState) => state.allProductSuggestions.message;
export const selectSuggestionsIsLocked = (state: RootState) => state.allProductSuggestions.isLocked;
export const selectSuggestionsLoadingPhase = (state: RootState) => state.allProductSuggestions.loadingPhase;
export const selectSuggestionsSearchCitations = (state: RootState) => state.allProductSuggestions.searchCitations;
export const selectSuggestionsScoreCandidates = (state: RootState) => state.allProductSuggestions.scoreCandidates;
export const selectSuggestionsSkipReasonCode = (state: RootState) => state.allProductSuggestions.skipReasonCode;
export const selectSuggestionsPendingAction = (state: RootState) => state.allProductSuggestions.pendingAction;
export const selectSuggestionsAllProducts = (state: RootState) => state.allProductSuggestions.allProducts;

// Template Content Selectors
export const selectTemplateContents = (state: RootState) => state.templateContents.templateContents;
export const selectTemplateTitles = (state: RootState) => state.templateContents.templateTitles;
export const selectUserDefaultContent = (state: RootState) => state.templateContents.userDefaultContent;
export const selectSystemDefaultContent = (state: RootState) => state.templateContents.systemDefaultContent;
export const selectSelectedProduct = (state: RootState) => state.templateContents.selectedProduct;

// Chat Component Selectors
export const selectCurrentSessionId = (state: RootState) => state.chatComponent.currentSessionId;
export const selectOverAllCurrentSessionDataPoints = (state: RootState) => state.chatComponent.overAllCurrentSessionDataPoints;

// Feedback Page Selectors
export const selectFeedbackCurrentSessionId = (state: RootState) => state.feedBackPage.currentSessionId;
export const selectFeedbackOverAllDataPoints = (state: RootState) => state.feedBackPage.overAllCurrentSessionDataPoints;

// Memoized Selectors (computed values)
export const selectSessionDataPoints = createSelector(
    [selectOverAllCurrentSessionDataPoints, selectFeedbackOverAllDataPoints, (_state: RootState, isFeedbackPage: boolean) => isFeedbackPage],
    (chatDataPoints, feedbackDataPoints, isFeedbackPage) => (isFeedbackPage ? feedbackDataPoints : chatDataPoints)
);

export const selectCurrentSessionIdByContext = createSelector(
    [selectCurrentSessionId, selectFeedbackCurrentSessionId, (_state: RootState, isFeedbackPage: boolean) => isFeedbackPage],
    (chatSessionId, feedbackSessionId, isFeedbackPage) => (isFeedbackPage ? feedbackSessionId : chatSessionId)
);

// Template selectors with memoization
export const selectActiveTemplate = createSelector(
    [selectTemplateContents, selectSelectedProduct, selectUserDefaultContent],
    (contents, product, userDefault) => {
        if (!product || !userDefault) return null;
        const templateId = (userDefault as any)[product];
        return contents.find((t: any) => t.id === templateId);
    }
);

export const selectDefaultTemplateForProduct = createSelector(
    [selectTemplateContents, (_state: RootState, product: string) => product],
    (contents, product) => {
        if (product === "ALL") {
            return contents.find((t: any) => t.product === "ALL" && t.template?.[0]?.isDefault === true);
        }
        return contents.find((t: any) => t.template?.[0]?.isDefault === true);
    }
);
